/**
 * Shared role configuration — interfaces, constants, and utility functions
 * used by both user-profile and bulk-assign components.
 */

// --- Interfaces ---

export interface UserRole {
  id: string;
  group_id: string;
  group_name: string;
  reporting_unit: { id: number; code: string; name: string } | null;
  department: { id: number; code: string; name: string; description?: string | null } | null;
  department_institution: string; // Display string derived from RU/dept name or access hint
  isEditing?: boolean; // For placeholder rows
}

export interface Group {
  id: string;
  type: string;
  attributes: {
    name: string;
    permission_count: number;
  };
}

export interface Department {
  id: string;
  type: string;
  attributes: {
    code: string;
    name: string;
    description: string | null;
    is_active: boolean;
  };
}

export interface ReportingUnit {
  id: string;
  type: string;
  attributes: {
    code: string;
    name: string;
    description: string | null;
    is_active: boolean;
  };
}

export interface ScopeOption {
  value: string;
  label: string;
  type: 'department' | 'reporting_unit';
}

// --- Constants ---

/** Groups that require admin-configurable scope (dept or RU selection) */
export const SCOPED_GROUPS = ['department_center_level', 'school_institute_libraries_level'];

/** Access hints per group name */
export const ACCESS_HINTS: Record<string, string> = {
  'academic_appoint_admin': 'Access to all reporting units (Morningside and CUIMC)',
  'academic_appoint_user': 'Access to all reporting units (Morningside and CUIMC)',
  'academic_appoint_user_cuimc': 'Access to CUIMC reporting units: P&S Basic (14), P&S Clinical (15), Public Health (16), Nursing (17), Dental Medicine (18)',
  'department_center_level': 'Access scoped to selected department or reporting unit',
  'school_institute_libraries_level': 'Selecting a reporting unit grants access to all departments within it',
  'vice_provost_faculty_affairs': 'Reviews leave requests after the Academic Appointments stage, across all reporting units. No officer records, PAC feed, reports, or user management.',
  'faculty_affairs': 'Access to all reporting units (Morningside and CUIMC)',
  'provost_office': 'Access to all reporting units (Morningside and CUIMC)',
  'cfo': 'Pre-defined access scope',
};

/** Role display tiers for header ordering (1 = widest, 3 = narrowest) */
export const ROLE_TIERS: Record<string, number> = {
  'academic_appoint_admin': 1,
  'academic_appoint_user': 1,
  'academic_appoint_user_cuimc': 1,
  'vice_provost_faculty_affairs': 1,
  'faculty_affairs': 1,
  'provost_office': 1,
  'cfo': 1,
  'school_institute_libraries_level': 2,
  'department_center_level': 3,
  'academic_officer_faculty': 4,
  'academic_officer_non_faculty': 4,
};

/** Short human-readable labels for group names */
export const GROUP_DISPLAY_NAMES: Record<string, string> = {
  'academic_appoint_admin': 'Academic Appointment Admin',
  'academic_appoint_user': 'Academic Appointment User',
  'academic_appoint_user_cuimc': 'Academic Appointment User (CUIMC)',
  'vice_provost_faculty_affairs': 'Vice Provost for Faculty Affairs',
  'faculty_affairs': 'Faculty Affairs',
  'provost_office': 'Provost Office',
  'cfo': 'CFO',
  'school_institute_libraries_level': 'Second Level User (Reviewer)',
  'department_center_level': 'First Level User (Initiator)',
};

// --- Utility Functions ---

/** Human-readable label for a group name, falling back to the raw name */
export function getGroupDisplayName(groupName: string): string {
  return GROUP_DISPLAY_NAMES[groupName] ?? groupName;
}

/** Check if a group requires scope selection (department/reporting unit) */
export function needsScopeSelection(groupId: string, availableGroups: Group[]): boolean {
  const group = availableGroups.find(g => g.id === groupId);
  return !!group && SCOPED_GROUPS.includes(group.attributes.name);
}

/**
 * Returns which scope type the group expects.
 * - 'department'      → department_center_level: show departments (excluding Zuckerman Institute)
 * - 'reporting_unit'  → school_institute_libraries_level: show reporting units (includes Zuckerman)
 */
export function getScopeTypeForGroup(groupName: string): 'department' | 'reporting_unit' {
  if (groupName === 'school_institute_libraries_level') return 'reporting_unit';
  return 'department';
}

/**
 * Explains why a saved role is unusable, or null when it is fine.
 *
 * The two scoped roles are only meaningful with their scope attached — review
 * permissions are granted by matching a role's department or reporting unit
 * against the officer's. A scoped role stored without one grants nothing and
 * cannot be repaired in place, so the fix is to remove and re-add it.
 */
export function getRoleScopeIssue(role: UserRole): string | null {
  // Rows still being configured in the UI are not saved roles yet.
  if (role.isEditing) return null;

  if (role.group_name === 'school_institute_libraries_level' && !role.reporting_unit) {
    return 'This Second Level User (Reviewer) role has no reporting unit, so it grants no access. '
      + 'Remove it and add it again with a reporting unit selected.';
  }
  if (role.group_name === 'department_center_level' && !role.department) {
    return 'This First Level User (Initiator) role has no department, so it grants no access. '
      + 'Remove it and add it again with a department selected.';
  }
  return null;
}

/** True when a saved role is missing the scope its group requires. */
export function hasRoleScopeIssue(role: UserRole): boolean {
  return getRoleScopeIssue(role) !== null;
}

/** Get access hint text for a group */
export function getAccessHint(groupId: string, availableGroups: Group[]): string {
  const group = availableGroups.find(g => g.id === groupId);
  if (!group) return '';
  return ACCESS_HINTS[group.attributes.name] || '';
}

/** Resolve autocomplete code value to numeric IDs for assign/remove API calls */
export function resolveScope(
  departmentInstitution: string,
  departments: Department[],
  reportingUnits: ReportingUnit[]
): { reportingUnitId: number | null; departmentId: number | null } {
  if (!departmentInstitution) return { reportingUnitId: null, departmentId: null };

  // Check reporting units first
  const ru = reportingUnits.find(r => r.attributes.code === departmentInstitution);
  if (ru) {
    return { reportingUnitId: parseInt(ru.id), departmentId: null };
  }

  // Check departments
  const dept = departments.find(d => d.attributes.code === departmentInstitution);
  if (dept) {
    return { reportingUnitId: null, departmentId: parseInt(dept.id) };
  }

  return { reportingUnitId: null, departmentId: null };
}

/** Filter scope options by query string */
export function filterScopeOptions(query: string, allOptions: ScopeOption[]): ScopeOption[] {
  const q = (query || '').toLowerCase();
  if (!q) return allOptions;
  return allOptions.filter(opt => opt.label.toLowerCase().includes(q));
}

/** Filter groups to exclude academic officer roles (not admin-assignable) */
export function getAssignableGroups(groups: Group[]): Group[] {
  return groups.filter(g =>
    g.attributes.name !== 'academic_officer_faculty' &&
    g.attributes.name !== 'academic_officer_non_faculty'
  );
}

/**
 * Map an API 1 embedded role object to the UserRole interface.
 * API 1 role shape: { id, group_id, group_name, reporting_unit: {id,code,name}|null, department: {id,code,name}|null }
 */
export function mapApiRoleToUserRole(apiRole: any): UserRole {
  const ru = apiRole.reporting_unit || null;
  const dept = apiRole.department || null;

  let displayStr = '';
  if (ru && dept) {
    displayStr = `${ru.name} / ${dept.description || dept.name}`;
  } else if (ru) {
    displayStr = ru.name;
  } else if (dept) {
    displayStr = dept.description || dept.name;
  } else {
    displayStr = ACCESS_HINTS[apiRole.group_name] || '';
  }

  return {
    id: apiRole.id?.toString() || '',
    group_id: apiRole.group_id?.toString() || '',
    group_name: apiRole.group_name || '',
    reporting_unit: ru,
    department: dept,
    department_institution: displayStr,
    isEditing: false,
  };
}

/**
 * Find duplicate roles between staged roles and a user's existing roles.
 * For scoped roles, both group_name AND scope (RU/dept) must match.
 */
export function findDuplicateRoles(
  stagedRoles: UserRole[],
  existingRoles: UserRole[],
  availableGroups: Group[],
  departments: Department[],
  reportingUnits: ReportingUnit[]
): { roleName: string; scope: string }[] {
  const duplicates: { roleName: string; scope: string }[] = [];

  for (const staged of stagedRoles) {
    if (staged.isEditing || !staged.group_id) continue;

    const group = availableGroups.find(g => g.id === staged.group_id);
    if (!group) continue;

    for (const existing of existingRoles) {
      if (existing.group_name !== group.attributes.name) continue;

      if (SCOPED_GROUPS.includes(group.attributes.name)) {
        // For scoped roles, also match RU/dept
        const stagedScope = resolveScope(staged.department_institution, departments, reportingUnits);
        const sameRU = stagedScope.reportingUnitId != null && stagedScope.reportingUnitId === existing.reporting_unit?.id;
        const sameDept = stagedScope.departmentId != null && stagedScope.departmentId === existing.department?.id;
        if (sameRU || sameDept) {
          const scopeLabel = existing.reporting_unit?.name || existing.department?.description || existing.department?.name || '';
          duplicates.push({ roleName: group.attributes.name, scope: scopeLabel });
        }
      } else {
        // Non-scoped: group_name match alone is a duplicate
        duplicates.push({ roleName: group.attributes.name, scope: '' });
      }
    }
  }

  return duplicates;
}
